import React, {Component} from 'react';
import PropTypes from 'prop-types';
import styled, {css} from 'styled-components';
import {connect} from 'react-redux';
import {getFormData} from '@nexios/frontend-forms';
import _ from 'lodash';

const Icon = styled.div`
  ${props => props.theme.button.icon || 'display: none;'}
`;

const Btn = styled.button`
  width: 100%;
  height: ${props => props.theme.button.height};
  line-height: ${props => props.theme.button.height};
  font-family: ${props => props.theme.button.fontFamily};
  font-size: ${props => props.theme.personalMessage ? props.theme.personalMessage.buttonFontSize : props.theme.button.fontSize};
  font-style: normal;
  font-weight: ${props => props.theme.button.fontWeight};
  letter-spacing: 0.6px;
  text-align: center;
  color: ${props => props.theme.button.color};
  border-radius: ${props => props.theme.button.borderRadius};
  background-color: ${props => props.theme.button.backgroundColor};
  border: 1px solid transparent;
  border-bottom: ${props => props.theme.button.borderBottom};
  box-shadow: ${props => props.theme.button.boxShadow};
  cursor: pointer;
  padding: ${props => props.theme.button.padding};
  display: ${props => props.theme.button.display};
  align-items: ${props => props.theme.button.alignItems};
  justify-content: ${props => props.theme.button.justifyContent};
  gap: ${props => props.theme.button.gap};
  margin: ${props => props.withMargin && props.theme.button.margin};
  transition: ${props => props.theme.button.transition || 'none'};

  &:active {
    ${props => props.theme.button.active};
  }

  &:focus {
    ${props => props.theme.button.focus};
  }

  &:hover {
    ${props => props.theme.button.hover};

    ${Icon} {
      ${props => props.theme.button.iconHover};
    }
  }

  ${props => props.looksDisabled && props.theme.button.backgroundColorDisabled && `
    color: ${props.theme.button.colorDisabled};
    background-color: ${props.theme.button.backgroundColorDisabled};
    border: ${props.theme.button.borderDisabled};
    &:hover  {
      background-color: ${props.theme.button.backgroundColorDisabled};
      box-shadow: none;
      ${Icon} {
        transform: none;
      }
    }
    &:active {
      background-color: ${props.theme.button.backgroundColorDisabled};
    }
  `}
`;

const primary = '#F7CC26';
const hover = '#F8D651';
Btn.defaultProps = {
  theme: {
    button: {
      padding: '1px 6px',
      borderRadius: '0px',
      fontFamily: '"Maax Bold", "Arial" ,"sans-serif"',
      fontSize: '20px',
      height: '56px',
      backgroundColor: primary,
      color: '#000',
      boxShadow: 'none',
      active: css`
        background-color: ${hover};
        border-bottom: none;
        box-shadow: none !important;
      `,
      hover: css`
        background-color: ${hover};
      `,
      focus: css`
        box-shadow: none;
      `
    }
  }
};

class Button extends Component {
  static propTypes = {
    label: PropTypes.string.isRequired,
    disabled: PropTypes.bool,
    complete: PropTypes.bool,
    onClick: PropTypes.func
  };

  render () {
    const disabled = this.props.hasErrors;
    const looksDisabled = this.props.complete === false;

    if (disabled) {
      return (
        <Btn
          looksDisabled={looksDisabled}
          disabled={this.props.disabled}>
          {this.props.label}<Icon />
        </Btn>
      );
    }

    return (
      <Btn
        looksDisabled={looksDisabled}
        onClick={this.props.onClick}
        disabled={this.props.disabled}
        withMargin={this.props.withMargin}>
        {this.props.label}<Icon />
      </Btn>
    );
  }
}

const mapStateToProps = (state, ownProps) => {
  const {formConfig, formInstanceKey, formStateKey} = ownProps;

  if (!formConfig || !formConfig.fields || !formInstanceKey) {
    return {complete: true};
  }

  const formData = getFormData(state, formInstanceKey, formStateKey);
  const ctx = {...ownProps.formContext, ...formData};

  // Button looks disabled if form is not complete
  const complete = Object.keys(formConfig.fields).every(field => {
    const cfg = formConfig.fields[field];

    let isVisible = true;
    try {
      if (_.isFunction(cfg.isVisible)) {
        isVisible = cfg.isVisible(ctx) !== false;
      } else if (!_.isNil(cfg.isVisible)) {
        isVisible = cfg.isVisible !== false;
      }
    } catch (e) {
      isVisible = true;
    }
    if (!isVisible) {
      return true;
    }

    return (cfg.rules || []).every(rule => {
      if (!_.isFunction(rule.isValid)) {
        return true;
      }
      // Skip async rules (isValid(ctx, callback)); they can't be evaluated synchronously.
      if (rule.isValid.length === 2) {
        return true;
      }
      try {
        return rule.isValid(ctx) === true;
      } catch (e) {
        return true;
      }
    });
  });

  return {complete, withMargin: true};
};

export default connect(mapStateToProps)(Button);

export const UnconnectedButton = Button;
